import { callAPI } from '../../../services/api.service';
import { debounce } from '../../../helper/Debounce';
import { CONFIG, StoryStepsEnum } from '../../../config/mainConfig';
import { Toast } from '../../../components/Common/Toast';
import { BookModel, IPageDataModel, StoryCreationMethodEnum, StoryLanguageTypeEnum } from '../../../interfaces/book.interface';
import { CharacterModel, StoryCharacterAgeUnitEnum, StoryCharacterEnum } from '../../../interfaces/character.interface';
import { Loaders, SetLoaders } from '../../../interfaces/common.interface';
import { ArtStyleModel } from '../../../interfaces/story.interface';
import { UserLocalStorageKeysEnum, UserModel, UserRoleEnum } from '../../../interfaces/user.interface';
import { AppDispatch } from '../../../store/store';
import { setStoryData } from '../../../store/storySlice';
import {
  openPlanPurchaseModal,
  setBookData,
  setBookId,
  setBookSettingsAge,
  setCurrentStep,
} from '../../../store/appSlice';
import { setSingleBookPlan } from '../../../store/userSlice';
import { ErrorModulesEnum } from '../../../interfaces/error.interface';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { isUserRole } from '../../../services/user.service';

export const checkSubscribedToPlan = async (
  user: UserModel,
  setLoaders: any,
  setPlanSubscribed: (plan: boolean) => void,
  loaders: Loaders,
  navigateTo: (url: string) => void,
  dispatch: AppDispatch,
) => {
  try {
    const response = await callAPI('api/checkUserSubscription', { email: user?.email });
    if (response?.subscribedToPlan) {
      setPlanSubscribed(true);
      setLoaders({ ...loaders, planExist: true });
      // if (response.hasOwnProperty('completedProfileShippingAddress') && !response.completedProfileShippingAddress && user) {
      //   dispatch(openIsCompleteProfileModalVisible({
      //     show: true,
      //     data: {
      //       title: 'Hey, you're account has been activated as a Teacher Account!',
      //       description: 'In order to continue, please provide your default ship-to location.',
      //       unclosable: true,
      //       showLogOut: true
      //     }
      //   }));
      // }
    } else {
      setPlanSubscribed(false);
      setLoaders({ ...loaders, planExist: false });
      if (response.forceShowSubscribeOnPlan && user?.role && isUserRole(user.role)) {
        dispatch(
          openPlanPurchaseModal({
            show: true,
            data: {
              title: 'Purchase plan!',
              description:
                "Order a physical copy of the book you're creating, or subscribe to a limited-time free trial to finish your current story and start new ones!",
              unclosable: true,
              showLogOut: true,
            },
          }),
        );
      }
      if (!user.planRef && user.subscribedOnPlanAt) {
        if (user.role && [UserRoleEnum.teacher].includes(user.role)) {
          navigateTo('/classroom/library');
        } else {
          navigateTo('/library');
        }
      }
    }
  } catch (error) {
    Toast('Failed to get plans', true);
    setPlanSubscribed(false);
    setLoaders({ ...loaders, planExist: false });
  }
};

export const getstoryConfig = async (setStoryConfig: (style: string) => void, dispatch: AppDispatch) => {
  try {
    const response = await callAPI(`api/story-config`);
    if (response && response?.data[0]) {
      setStoryConfig(response?.data[0]);
      dispatch(setBookSettingsAge(response?.data[0]));
    } else Toast('Failed to get story config', true);
  } catch (error) {
    Toast('Failed to get story config', true);
  }
};

export const getArtStyles = async (setArtStyles: (artStyle: any) => void) => {
  try {
    const response = await callAPI(`artstyle`);
    if (response?.artStyle) setArtStyles(response?.artStyle);
    else Toast('Failed to get art styles', true);
  } catch (error) {
    Toast('Failed to get art styles', true);
  }
};

export const fetchBookData = async (bookId: string) => {
  try {
    const response = (await callAPI(`api/books/${bookId}`))?.data;
    return response;
  } catch (error) {
    return null;
  }
};

export const doStartImageGeneration = async (bookId: string, showErrorMsg = true) => {
  if (!bookId) {
    return;
  }

  try {
    const response = await callAPI(`api/generateImages/${bookId}`);
    if (response?.response?.status === HTTPStatusEnum.BAD_REQUEST) {
      if (showErrorMsg === true) Toast('Need Additional Image Credits', true);
      return -1;
    }
    return 1;
  } catch (error) {
    Toast('Unexpected Error Occurred', true);
    return 0;
  }
};

export const sortPages = (bookData: any, dispatchSetBookData: AppDispatch) => {
  if (!bookData || !bookData?.pages) return; //javi

  const sortedPages = Object.entries(bookData.pages).sort((a, b) => {
    const pageA = parseInt(a[0].replace('page', ''), 10);
    const pageB = parseInt(b[0].replace('page', ''), 10);
    return pageA - pageB;
  });

  const sortedPagesObject = sortedPages.reduce<Record<string, any>>((obj, [key, value]) => {
    obj[key] = value;
    return obj;
  }, {});

  dispatchSetBookData(setBookData({ ...bookData, pages: sortedPagesObject }));
};

export const getLatestError = async (bookId: string, errorModule: string, dispatch: AppDispatch) => {
  if (!bookId) return;

  dispatch(setStoryData({ gettingError: true }));

  const latestError = await callAPI(`api/error-log?bookId=${bookId}&module=${errorModule}`);
  if (latestError?.error !== null) {
    // Glanceable toast for unknown/transient failures. Moderation errors
    // are suppressed upstream (see Builder.tsx + getCharacterImage); they
    // surface via their own inline banner + a moderation-specific toast
    // fired in `handleSubmitStepTwo`.
    Toast('Something went wrong. Please try again.', true);
  }
  dispatch(setStoryData({ gettingError: false }));
};

export const getCharacterImage = async (
  bookId: any,
  setLoaders: any,
  loaders: any,
  setCurrentPortrait: any,
  setCharacterOutlineByUser: any,
  setCurrentPortraitList: any,
  setCharacterGenerationProgress: any,
  setStartInterval: any,
  bookData: BookModel | null = null,
  storyData: BookModel | null = null,
  errorModule = ErrorModulesEnum.CHARACTER_GENERATION,
  dispatch: AppDispatch,
) => {
  try {
    setLoaders({ ...loaders, characterGenerating: true });
    try {
      if (bookData) {
        if (bookData?.characterPortrait) {
          if (storyData?.illustrationCharacter?.pageData?.imagePromptCharacterUrl)
            setCurrentPortrait(storyData?.illustrationCharacter?.pageData?.imagePromptCharacterUrl);
          else setCurrentPortrait(bookData?.characterPortrait || '');

          // The local form is the source of truth for the outline — we
          // autosave it to bookData on every edit and on submit. Pushing
          // `bookData.characterOutlineByUser` back into local state here
          // can only clobber in-flight edits (the listener fires before
          // a fire-and-forget autosave has landed, so bookData briefly
          // holds the previous outline). The previous code did this only
          // for the no-error branch; we removed it entirely.
          setCurrentPortraitList(bookData?.currentCharacterPortraitList || []);
          setLoaders({ ...loaders, characterGenerating: false, characterRegenerate: false });
          if (bookData?.isImageProcessing === true) setCharacterGenerationProgress(bookData?.progress);
          else {
            setCharacterGenerationProgress(0);
            dispatch(setStoryData({ characterBuilding: false }));
          }

          setStartInterval(false);
        } else if (bookData?.progress) {
          // Only set progress from bookData when characterPortrait doesn't exist yet
          // (first-ever generation). Otherwise the stale progress value (100) from
          // a previous completed generation would override the reset to 0 above.
          setCharacterGenerationProgress(bookData?.progress);
        }
        if (bookData?.error) {
          // Mirror the scene-side rule (Builder.tsx): when the per-screen
          // UI fully explains the failure (moderation banner with guidance),
          // don't also fire the generic "image generation failed" toast.
          const handledByCharacterBanner = !!bookData?.characterContentFilter?.flagged;
          if (!handledByCharacterBanner) {
            getLatestError(bookId, errorModule, dispatch);
          }
          dispatch(
            setStoryData({
              ...storyData,
              characterBuilding: false,
              creditsPerBookLimit: +(storyData?.creditsPerBookLimit ?? 0) + CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE,
            }),
          );
          setCharacterGenerationProgress(0);
        }
      }
      setLoaders({ ...loaders, characterGenerating: false, characterRegenerate: false });
    } catch (error) {
      setLoaders({ ...loaders, characterGenerating: false, characterRegenerate: false });
      dispatch(setStoryData({ characterBuilding: false }));
    }
  } catch (error) { }
};

export const handleSubmitStepTwo = async (
  bookId: string,
  selectedFile: string | File | null,
  artStyle: ArtStyleModel | null,
  characterOutlineByUser: Record<string, any>,
  setLoaders: (loaders: { [key: string]: boolean }) => void,
  loaders: { [key: string]: boolean },
  setCharacterDescription: (description: string) => void,
  setUploadedImageUrl: (url: string) => void,
  setImageUploading: (isUploading: boolean) => void,
  setStartInterval: (interval: boolean) => void,
  setCharacterOutlineByUser: (outline: Record<string, any>) => void,
  setCurrentPortrait: (portrait: string | null) => void,
  setCurrentPortraitList: (list: string[]) => void,
  continueWithoutImage = false,
  storyData: BookModel | null = null,
  dispatch: AppDispatch,
) => {
  if (!bookId) {
    return;
  }
  if (characterOutlineByUser) {
    if (characterOutlineByUser?.characterType === StoryCharacterEnum.human)
      setCharacterOutlineByUser((prevState: BookModel) => ({
        ...prevState,
        character: StoryCharacterEnum.human,
      }));
  }

  const characterOutline: any = characterOutlineByUser ? JSON.stringify(characterOutlineByUser) : {};
  let imageUrl: string | File | null | any = '';
  if (selectedFile !== './Image holder Icon.png') imageUrl = selectedFile;
  const formData = new FormData();
  formData.append('image', continueWithoutImage ? '' : imageUrl!);
  formData.append('bookId', bookId);
  formData.append('artStyle', artStyle?.id!);
  formData.append('characterOutlineByUser', characterOutline);
  formData.append('stableDifusionAPI', String(true));

  formData.append('continueWithoutImage', String(continueWithoutImage || !imageUrl));
  if (storyData?.illustrationCharacter?.pageIndex) {
    formData.append('illustrationCharacter', storyData?.illustrationCharacter?.pageIndex?.toString());
  }

  try {
    dispatch(setStoryData({ characterBuilding: true }));

    setLoaders({ ...loaders, characterRegenerate: true });
    let headers = {} as { Authorization: string };
    // const token = TokenCookieManager.getToken();
    // if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/uploadImage`, {
      method: 'POST',
      headers,
      credentials: 'include', // Always include cookies
      body: formData,
    });

    const data = await response.json();
    if (response.ok) {
      if (data?.status === HTTPStatusEnum.OK) {
        dispatch(
          setStoryData({
            regeneratedNewCharacter: true,
            // Only preserve illustrationCharacter when user is editing a specific
            // page's character (has pageIndex). Without this guard, first-time
            // character generation creates a truthy { isPortraitDeleted: false }
            // object that causes the step-3 useEffect to skip the api/magic call.
            ...(storyData?.illustrationCharacter?.pageIndex
              ? { illustrationCharacter: { ...storyData.illustrationCharacter, isPortraitDeleted: false } }
              : {}),
          }),
        );
        setLoaders({ ...loaders, characterRegenerate: true });

        setCurrentPortrait('');
        setCurrentPortraitList([]);

        setCharacterDescription(data?.characterDescription);
        setUploadedImageUrl(data?.uploadedImageUrl);

        setStartInterval(true);
        setImageUploading(false);
      } else {
        dispatch(
          setStoryData({
            creditsPerBookLimit: +(storyData?.creditsPerBookLimit ?? 0) + CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE,
          }),
        );
        setImageUploading(false);
        Toast('Something went wrong. Please try again.', true);
      }
    } else if (response.status === HTTPStatusEnum.BAD_REQUEST) {
      dispatch(
        setStoryData({
          creditsPerBookLimit: +(storyData?.creditsPerBookLimit ?? 0) + CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE,
        }),
      );
      if (data.errorType === 'character_regenerate_limit') {
        Toast(
          `Sorry, you have reached the limit of ${CONFIG.BOOK.CHARACTER_REGENERATE_LIMIT} characters. You can still access all characters you’ve created so far by clicking on ‘Character Library’.`,
          true,
        );
      } else if (data?.blockedBy) {
        // Moderation block — keep the toast short, the inline red banner
        // on the character page renders the full guidance and the
        // description field is highlighted. The toast is a glanceable
        // acknowledgement; the banner is the source of truth for what to fix.
        Toast("Couldn't generate this character — see details below", true);
      } else {
        Toast('Something went wrong. Please try again.', true);
      }
      setLoaders({ ...loaders, characterRegenerate: false });
    } else {
      Toast('Something went wrong. Please try again.', true);
      setLoaders({ ...loaders, characterRegenerate: false });
      dispatch(
        setStoryData({
          creditsPerBookLimit: +(storyData?.creditsPerBookLimit ?? 0) + CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE,
        }),
      );
    }
  } catch (error) {
    setLoaders({ ...loaders, characterRegenerate: false });
    dispatch(
      setStoryData({
        creditsPerBookLimit: +(storyData?.creditsPerBookLimit ?? 0) + CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE,
      }),
    );
  } finally {
    setImageUploading(false);
  }
};

export const getDraftDetails = async (
  draftBookId: string,
  dispatchSetBookId: AppDispatch,
  dispatchSetCurrentStep: AppDispatch,
  setShowStoryForm: (show: boolean) => void,
  setUploadedImageUrl: (url: string) => void,
  setCharacterDescription: (description: string) => void,
  // setStoryPages: (pages: StoryPage[]) => void,
  setBookTitle: (title: string) => void,
  // setBookSummary: (summary: string) => void,
  setStoryType: (type: string) => void,
  setChildName: (name: string) => void,
  setChildAge: (age: number) => void,
  setMaxpages: (maxPages: number) => void,
  setPageTotal: (totalPages: number) => void,
  setWritingStyle: (style: string) => void,
  setSelectedArtStyle: (style: string) => void,
  setStoryLanguage: (language: StoryLanguageTypeEnum) => void,
  setWordRange: (range: [number, number]) => void,
  setCurrentPortrait: (portrait: string) => void,
  setCurrentPortraitList: (list: string[]) => void,
  setCharacterOutlineByUser: (outline: Record<string, any>) => void,
  setSelectedImage: (image: string) => void,
  setSelectedStoryConfig: (config: any) => void,
  sortPages: (data: BookModel, setData: (type: string) => void) => any,
  dispatchSetBookData: AppDispatch,
  storyconfig: Record<string, any>,
  setStoryTopic: (topic: string) => void,
  setCurrentStoryStep: ((step: StoryStepsEnum | null) => void) | null = null,
  dispatch: AppDispatch,
  setStoryCreationMethod: (method: StoryCreationMethodEnum) => void,
  setBookStoryPagesData: (pages: { [pageName: string]: IPageDataModel }) => void,
) => {
  try {
    const data = draftBookId && (await callAPI(`api/getBookDetails/${draftBookId}`));
    setShowStoryForm(true);
    dispatchSetBookId(setBookId(draftBookId));

    console.log('DRAFT - ', data);

    if (data) {
      if (
        data?.book?.step === 3 ||
        localStorage.getItem(UserLocalStorageKeysEnum.STORY_STEP) === CONFIG.STORY_STEPS.ILLUSTRATION_CREATE
      ) {
        dispatchSetCurrentStep(setCurrentStep(3));
        setCurrentStoryStep &&
          setCurrentStoryStep(
            (localStorage.getItem(UserLocalStorageKeysEnum.STORY_STEP) as StoryStepsEnum) ||
            (CONFIG.STORY_STEPS.ILLUSTRATION_CREATE as StoryStepsEnum),
          );
      } else {
        dispatchSetCurrentStep(setCurrentStep(data?.book?.step));
        if (setCurrentStoryStep) {
          switch (data.book?.step) {
            case 1: {
              setCurrentStoryStep(CONFIG.STORY_STEPS.BASIC_INFO);
              break;
            }
            case 2: {
              setCurrentStoryStep(CONFIG.STORY_STEPS.CHARACTER_BUILD);
              break;
            }
            case 3: {
              setCurrentStoryStep(CONFIG.STORY_STEPS.ILLUSTRATION_CREATE);
              break;
            }
            case 6: {
              setCurrentStoryStep(CONFIG.STORY_STEPS.PURCHASE_DOWNLOAD);
              break;
            }
          }
        }
      }
    } else {
      dispatchSetCurrentStep(setCurrentStep(1));
      setCurrentStoryStep && setCurrentStoryStep(CONFIG.STORY_STEPS.BASIC_INFO);
    }

    setShowStoryForm(true);
    setUploadedImageUrl(data?.book?.uploadedImageUrl ? data?.book?.uploadedImageUrl : '');
    setCharacterDescription(data?.book?.characterDescription ? data?.book?.characterDescription : '');
    setStoryTopic(data?.book?.storyTopic ? data?.book?.storyTopic : '');
    setBookTitle(data?.book?.title ? data?.book?.title : '');
    setStoryType(data?.book?.storyType || '');

    setChildName(data?.book?.childName || '');
    setChildAge(data?.book?.childAge || '');
    setMaxpages(data?.book?.pageTotal);
    setPageTotal(data?.book?.pageTotal || '');
    // setSelectedStoryConfig(storyconfig[data?.book?.childAge]);

    setWritingStyle(data?.book?.writingStyle || '');

    setStoryLanguage(data?.book?.storyLanguage || 'english');
    setStoryCreationMethod(data?.book?.storyCreationMethod || 'ai-assisted');
    setWordRange(data?.book?.wordRange || storyconfig[data?.book?.childAge]?.words_total);
    setUploadedImageUrl(data?.book?.uploadedImageUrl || '');
    setCurrentPortrait(data?.book?.characterPortrait || '');
    setCurrentPortraitList(data?.book?.currentCharacterPortraitList || []);
    const serverPages = data?.book?.pages || {};
    const allPages: { [pageName: string]: IPageDataModel } = {};
    for (let i = 1; i <= CONFIG.ONE_STORY_TEXT_PAGES; i++) {
      const key = `page${i}`;
      allPages[key] = serverPages[key] || { text: '', index: i } as IPageDataModel;
    }
    setTimeout(() => setBookStoryPagesData(allPages));
    dispatch(setStoryData({ creditsPerBookLimit: data?.book?.creditsPerBookLimit }));

    if (data?.book?.artStyle) {
      const artStyleInfo = await callAPI(`api/artstyle-detail/${data?.book?.artStyle}`);
      setSelectedArtStyle(artStyleInfo ? { ...artStyleInfo?.message?.artStyle?.[0], id: data?.book?.artStyle } : '');
    }
    setSelectedImage(data?.book?.uploadedImageUrl || null);
    setSelectedStoryConfig(data?.book?.childAge || null);

    // Seed local outline from the saved draft. Firestore stores it as an
    // object, but historical writes may have stringified it; handle both.
    const rawOutline = data?.book?.characterOutlineByUser;
    let characterDescription: Record<string, any> | null = null;
    if (rawOutline) {
      try {
        characterDescription = typeof rawOutline === 'string' ? JSON.parse(rawOutline) : rawOutline;
      } catch {
        characterDescription = null;
      }
    }
    setCharacterOutlineByUser(
      characterDescription || { characterType: StoryCharacterEnum.human, ageUnit: StoryCharacterAgeUnitEnum.year },
    );
    dispatchSetBookData(setBookData(data?.book || null));
    sortPages(data?.book ? data?.book : null, dispatchSetBookData);
  } catch (error) {
    console.error('getDraftDetails failed:', error);
  }
};

export const getCharacterLibary = async (
  email: string,
  loaders: Loaders,
  setLoaders: SetLoaders,
  setLibrary: (data: any) => void,
) => {
  if (setLoaders) {
    setLoaders({ ...loaders, characterLibrary: true });
    const response = await callAPI(`api/character-library?email=${email}`);

    setLibrary(response?.data);
    setLoaders({ ...loaders, characterLibrary: true });
  }
};

export const resendVerificationEmail = async (
  loaders: Loaders,
  setLoaders: SetLoaders,
  email: string,
  firstName: string,
) => {
  setLoaders({ ...loaders, verificationEmail: true });
  const response = await callAPI(`user/resend-verification-email`, { email, firstName });
  if (response.status) Toast('Verification Email Sent Successfully!');
  else Toast('Verification Email Sent Failed!', true);
  setLoaders({ ...loaders, verificationEmail: false });
};

export const handleCouponChange = debounce(async (couponCode, amount, requestType) => {
  try {
    if (couponCode?.length >= 3) {
      const response = await callAPI('user/coupon-validate', {
        couponCode,
        amount,
        requestType,
      });

      if (response.status === HTTPStatusEnum.OK) {
        Toast('Coupon Applied');
      } else {
        Toast('Invalid Coupon', true);
      }
    }
  } catch (error) {
  } finally {
  }
}, 500);

export const saveCharacterForImagePrompt = async (
  bookId: string | undefined,
  characterUrl: string,
  pageIndex?: number | null,
  character?: CharacterModel,
  characterOutlineByUser?: any,
  uploadedImage?: string,
) => {
  if (!bookId) {
    return;
  }
  try {
    const selectedCharacter = character?.characterId ? { selectedCharacterId: character?.characterId } : {};
    await callAPI('api/book-update/update-page-characterPrompt-url', {
      characterUrl,
      bookId,
      ...selectedCharacter,
      pageIndex,
      characterOutlineByUser,
      uploadedImage,
    });
  } catch (error) { }
};

export const checkUserBookCreationStatus = async (email: string) => {
  const response = await callAPI('user/check-user-book-creation-status', { email });
  if (response?.status === HTTPStatusEnum.OK) return true;
  else if (response?.status === HTTPStatusEnum.FORBIDDEN) return false;
  else Toast('Something went wrong!', true);
};

export const archiveBook = async (bookId: string, fetchBooks: () => void, setLoader?: (value: boolean) => void) => {
  try {
    if (setLoader) setLoader(true);
    const response = await callAPI('api/book-update/archive-book', { bookId });
    if (response.status === HTTPStatusEnum.OK) {
      Toast(response?.message);
      fetchBooks();
    } else Toast(response?.message || response?.error, true);
    if (setLoader) setLoader(false);
  } catch (error) {
    Toast('Something Went Wrong!', true);
    if (setLoader) setLoader(false);
  }
};

export const unArchiveBook = async (bookId: string, fetchBooks: () => void, setLoader?: (value: boolean) => void) => {
  try {
    if (setLoader) setLoader(true);
    const response = await callAPI('api/book-update/unarchive-book', { bookId });
    if (response.status === HTTPStatusEnum.OK) {
      Toast(response?.message);
      fetchBooks();
    } else Toast(response?.message || response?.error, true);

    if (setLoader) setLoader(false);
  } catch (error) {
    Toast('Something Went Wrong!', true);
    if (setLoader) setLoader(false);
  }
};

export const handleDeleteBook = async (
  bookId: string,
  setLoader: (value: boolean) => void,
  navigateTo: (value: string) => void,
  handleClose?: () => void,
  handleFetchBooks?: () => void,
) => {
  setLoader(true);
  const response = await callAPI(`api/delete-book`, { bookId });
  if (response.status === 200) {
    localStorage.removeItem(UserLocalStorageKeysEnum.BOOK_ID);
    localStorage.removeItem(UserLocalStorageKeysEnum.EDIT_MODE);
    localStorage.removeItem(UserLocalStorageKeysEnum.STORY_STEP);
    Toast('Book Deleted Successfully');
    setLoader(false);
    navigateTo('/library');
    handleClose && handleClose();
    handleFetchBooks && handleFetchBooks();
  } else {
    Toast('Unexpected Error deleting book', true);
    setLoader(false);
  }
};

export const checkSingleBookPlan = async (planId: string, dispatchSetSingleBookPlan: AppDispatch) => {
  try {
    if (!planId) {
      return;
    }
    const response = await callAPI(`plans/check-single-book-plan/?planId=${planId}`);
    if (response.status === HTTPStatusEnum.OK && response?.message === true) {
      dispatchSetSingleBookPlan(setSingleBookPlan(true));
    } else {
      dispatchSetSingleBookPlan(setSingleBookPlan(false));
    }
  } catch (error) { }
};

export const updateBookForFreeMode = async (bookId: string) => {
  try {
    const response = await callAPI(`api/book-update/update-book-free-mode`, { bookId });
    if (response.status === HTTPStatusEnum.OK) {
      Toast('Relax Mode Activated');
    } else {
      Toast('Relax Mode failed! Try Again', true);
    }
  } catch (error) { }
};

export const convertSecondsToMinutes = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes} : ${remainingSeconds} `;
};

export const sortBookPages = (bookData: BookModel, setPages: (page: any) => void, isBlocked: boolean) => {
  if (bookData && bookData?.pages) {
    let defaultImage: string = '';
    const sortedPages = Object.entries(bookData.pages)
      .sort((a, b) => parseInt(a[0].replace('page', '')) - parseInt(b[0].replace('page', '')))
      .map((page, index) => {
        if (isBlocked && !defaultImage) {
          defaultImage = page[1].imageURLwebp || page[1].imageURL || '';
        }
        const block = isBlocked && +page[0].replace('page', '') >= CONFIG.TRIAL.START_PAGE_IMAGE_BLOCK;
        page[1] = {
          ...page[1],
          block,
          imageURLwebp: isBlocked && block ? defaultImage : page[1].imageURLwebp,
          imageURL: isBlocked && block ? defaultImage : page[1].imageURL,
        };
        return page;
      });
    setPages(sortedPages);
  }
};
